'use client';

import { useRef, useState, useEffect } from 'react';

/**
 * IntersectionObserver-based reveal hook.
 * Returns { ref, shown } — `shown` flips to true once and stays true.
 * Includes a timeout fallback to guarantee content becomes visible.
 */
export function useReveal(threshold = 0.14) {
  const ref = useRef<HTMLElement | null>(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const el = ref.current;

    // Timeout fallback: if IO never fires (e.g. headless browser),
    // force shown after 1.2s so content is always visible
    const fallback = setTimeout(() => setShown(true), 1200);

    if (!el) return () => clearTimeout(fallback);

    const io = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          clearTimeout(fallback);
          setShown(true);
          io.disconnect();
        }
      },
      { threshold, rootMargin: '0px 0px -6% 0px' }
    );
    io.observe(el);
    return () => {
      clearTimeout(fallback);
      io.disconnect();
    };
  }, [threshold]);

  return { ref, shown };
}
